import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsSources, ANALYTICS_SOURCE_LOGOS } from '../hooks/useAnalyticsSources';
import { serializeRangeState } from '../utils/productAnalysisUrlState';

const SOURCE_DEFAULTS = {
  shopify: { id: 'shopify', name: 'Shopify', path: '/app/product-analysis/analytics' },
  'meta-ads': { id: 'meta-ads', name: 'Meta Ads', path: '/app/product-analysis/analytics/meta-ads' },
  klaviyo: { id: 'klaviyo', name: 'Klaviyo', path: '/app/product-analysis/analytics/klaviyo' }
};

/**
 * Product Analysis page title: `{logo} {integration name}` as a dropdown of the store's active
 * analytics dashboards. Switching keeps the date range (carried in the URL) and drops filters,
 * which are source-specific.
 */
export default function AnalyticsSourceSelector({ current, activeRange, match, timezone }) {
  const navigate = useNavigate();
  const { sources } = useAnalyticsSources();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = sources.find((s) => s.id === current) || {
    ...SOURCE_DEFAULTS[current],
    logo: ANALYTICS_SOURCE_LOGOS[current] || null
  };
  const others = sources.filter((s) => s.id !== current);
  const switchable = others.length > 0;

  const label = (
    <>
      {active.logo && <img className="analytics-source__logo" src={active.logo} alt="" aria-hidden="true" />}
      <span>{active.name}</span>
    </>
  );

  if (!switchable) {
    return <div className="dashboard__title analytics-source">{label}</div>;
  }

  const go = (source) => {
    setOpen(false);
    const qs = serializeRangeState({ activeRange, match, timezone }).toString();
    navigate(qs ? `${source.path}?${qs}` : source.path);
  };

  return (
    <div className="analytics-source__wrapper" ref={ref}>
      <button
        type="button"
        className="dashboard__title analytics-source analytics-source__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span className={`analytics-source__caret${open ? ' analytics-source__caret--open' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="analytics-source__menu" role="listbox">
          {sources.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={s.id === current}
              className={`analytics-source__option${s.id === current ? ' analytics-source__option--active' : ''}`}
              onClick={() => (s.id === current ? setOpen(false) : go(s))}
            >
              {s.logo && <img className="analytics-source__logo" src={s.logo} alt="" aria-hidden="true" />}
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
